import type {
  NetworkGraphEdgeModel,
  NetworkGraphNodeModel,
} from '../NetworkGraph';
import type { NetworkGraphGeometry } from './buildGraphGeometry';
import type { NetworkGraphPosition } from './computeRadialLayout';
import { edgeLabelBox } from './networkGraphEdgeUtils';
import { nodeLabelBox, nodeOuterRadius } from './nodeGeometry';
import {
  type LabelBox,
  type LabelOffsets,
  resolveLabelCollisions,
} from './resolveLabelCollisions';

interface ResolveGraphLabelsOptions {
  nodes: readonly NetworkGraphNodeModel[];
  edges: readonly NetworkGraphEdgeModel[];
  positions: Readonly<Record<string, NetworkGraphPosition>>;
  // Layout placement rays (degrees, anchor→node); see nodeLabelBox.
  rayAngles: Readonly<Record<string, number>>;
  geometry: NetworkGraphGeometry;
}

export interface ResolvedGraphLabels {
  offsets: LabelOffsets;
  // Axis-aligned boxes at post-collision positions — feed these to fit-viewport
  // so ray labels (radius+gap+textWidth/2 beyond the node) are not clipped.
  boxes: Pick<LabelBox, 'x' | 'y' | 'width' | 'height'>[];
}

// Persistent labels only: hover-revealed node/edge labels fade in at their
// stable pre-resolved positions (native :hover / isHovered) instead of being
// re-placed by the collision solver, which would make labels jump and reshuffle
// neighbours. Keeping hover out also means no per-hover graph recompute.
export const resolveGraphLabels = ({
  nodes,
  edges,
  positions,
  rayAngles,
  geometry,
}: ResolveGraphLabelsOptions): ResolvedGraphLabels => {
  const labelBoxes: LabelBox[] = [];
  for (const node of nodes) {
    const position = positions[node.id];
    if (position && node.labelVisible) {
      labelBoxes.push(
        nodeLabelBox({
          node,
          position,
          connectedPositions: geometry.connectedPositions.get(node.id) ?? [],
          rayAngle: rayAngles[node.id],
        })
      );
    }
  }
  for (const edge of edges) {
    const source = geometry.nodeById.get(edge.sourceId);
    const target = geometry.nodeById.get(edge.targetId);
    const sourcePosition = positions[edge.sourceId];
    const targetPosition = positions[edge.targetId];
    if (
      edge.label &&
      edge.emphasis !== 'selected' &&
      edge.labelVisible &&
      source &&
      target &&
      sourcePosition &&
      targetPosition
    ) {
      labelBoxes.push(
        edgeLabelBox({
          edge,
          source,
          sourcePosition,
          target,
          targetPosition,
          targetAngleOffset: geometry.targetAngleOffsets.get(edge.id),
          lateralOffset: geometry.laneOffsets.get(edge.id),
        })
      );
    }
  }

  // No `bounds`: the layout legitimately extends past the canvas rectangle
  // (fit-to-content viewport scales it into view). Scoring candidates against
  // the layout-space rect made every candidate of an edge-of-ring label
  // "blocked", degrading the choice to least-overflow — which lane-shifted
  // labels off their arrow ray without fixing anything.
  const offsets = resolveLabelCollisions(labelBoxes, {
    circles: [
      ...nodes.flatMap(node => {
        const position = positions[node.id];

        return position
          ? [{ ...position, radius: nodeOuterRadius(node.size) }]
          : [];
      }),
      ...geometry.arrowheadCircles,
    ],
    segments: geometry.edgeSegments,
  });

  return {
    offsets,
    boxes: labelBoxes.map(box => ({
      x: box.x + (offsets[box.id]?.x ?? 0),
      y: box.y + (offsets[box.id]?.y ?? 0),
      width: box.width,
      height: box.height,
    })),
  };
};
